import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Table, Badge, Form, Button, Row, Col } from 'react-bootstrap';
import { getUserById, getUserTransactions, getUserTrades } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const UserDetails = () => {
    const { id } = useParams();
    const [user, setUser] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [trades, setTrades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('transactions');
    const [filters, setFilters] = useState({
        type: 'all',
        status: 'all',
        page: 1,
        limit: 10
    });

    useEffect(() => {
        fetchUserDetails();
    }, [id]);

    useEffect(() => {
        if (activeTab === 'transactions') {
            fetchTransactions();
        } else if (activeTab === 'trades') {
            fetchTrades();
        }
    }, [activeTab, filters]);

    const fetchUserDetails = async () => {
        try {
            setLoading(true);
            const response = await getUserById(id);
            if (response.success || response.data) {
                setUser(response.data || response);
            } else {
                throw new Error(response.message || 'Failed to fetch user details');
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
            setError(error.message || 'Failed to fetch user details');
        } finally {
            setLoading(false);
        }
    };

    const fetchTransactions = async () => {
        try {
            const response = await getUserTransactions(id, filters);
            if (response.success || response.data) {
                setTransactions(response.data?.transactions || response.data || []);
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
        }
    };

    const fetchTrades = async () => {
        try {
            const response = await getUserTrades(id, filters);
            if (response.success || response.data) {
                setTrades(response.data?.trades || response.data || []);
            }
        } catch (error) {
            console.error('Error fetching trades:', error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: 'success',
            inactive: 'warning',
            blocked: 'danger',
            pending: 'warning',
            completed: 'success',
            failed: 'danger',
            cancelled: 'secondary'
        };
        return badges[status] || 'primary';
    };

    if (loading && !user) {
        return (
            <div className="container-xxl">
                <div className="d-flex justify-content-center align-items-center min-vh-50">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !user) {
        return (
            <div className="container-xxl">
                <div className="alert alert-danger">{error}</div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="container-xxl">
                <div className="alert alert-info">No user data found</div>
            </div>
        );
    }

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="User Details" sidebutton={true} />
            <div className="row mb-3">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="text-primary mb-0">{user.name || 'N/A'}</h4>
                                    <p className="text-muted mb-0">User ID: {user._id}</p>
                                </div>
                                <Link to="/admin/users" className="btn btn-secondary">
                                    <i className="fa fa-arrow-left me-1"></i> Back to Users
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-xl-8">
                    <Card className="mb-3">
                        <Card.Header>
                            <Card.Title>Basic Information</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong className="text-dark">Email:</strong>
                                        <div className="d-flex align-items-center">
                                            {user.email || '-'}
                                            {user.isEmailVerified && (
                                                <i className="fa fa-check-circle text-success ms-2" title="Verified"></i>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <strong className="text-dark">Mobile Number:</strong>
                                        <div className="d-flex align-items-center">
                                            {user.mobileNumber || '-'}
                                            {user.isMobileVerified && (
                                                <i className="fa fa-check-circle text-success ms-2" title="Verified"></i>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <strong className="text-dark">Gender:</strong>
                                        <p className="text-capitalize">{user.gender || '-'}</p>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong className="text-dark">Status:</strong>
                                        <div>
                                            <Badge bg={getStatusBadge(user.status)}>
                                                {user.status || 'N/A'}
                                            </Badge>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <strong className="text-dark">Role:</strong>
                                        <p className="text-capitalize">{user.role || 'user'}</p>
                                    </div>
                                    <div className="mb-3">
                                        <strong className="text-dark">Member Since:</strong>
                                        <p>{formatDate(user.createdAt)}</p>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Header>
                            <Card.Title>Wallet & Transaction Statistics</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={4}>
                                    <div className="text-center">
                                        <div className="text-muted mb-1">Current Balance</div>
                                        <h3 className="text-primary">{formatAmount(user.wallet?.amount || user.wallet?.balance || 0)}</h3>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="text-center">
                                        <div className="text-muted mb-1">Total Deposits</div>
                                        <h3 className="text-success">{formatAmount(user.totalDeposits || 0)}</h3>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="text-center">
                                        <div className="text-muted mb-1">Total Withdrawals</div>
                                        <h3 className="text-danger">{formatAmount(user.totalWithdrawals || 0)}</h3>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>

                <div className="col-xl-4">
                    <Card className="mb-3">
                        <Card.Header>
                            <Card.Title>Referral Information</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-3">
                                <strong className="text-dark d-block mb-2">Referral Code</strong>
                                <div className="input-group">
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        value={user.referalCode || user.referralCode || '-'} 
                                        readOnly
                                    />
                                    <button 
                                        className="btn btn-primary" 
                                        onClick={() => {
                                            const code = user.referalCode || user.referralCode;
                                            if (code) {
                                                navigator.clipboard.writeText(code);
                                                alert('Referral code copied!');
                                            }
                                        }}
                                        type="button"
                                    >
                                        <i className="fa fa-copy"></i>
                                    </button>
                                </div>
                            </div>
                            <div className="mb-3">
                                <strong className="text-dark d-block mb-2">Referred By</strong>
                                <p>{user.referedBy || 'Not referred'}</p>
                            </div>
                            <div className="mb-3">
                                <strong className="text-dark d-block mb-2">Total Referrals</strong>
                                <div className="d-flex align-items-center">
                                    <h3 className="mb-0 me-2">{user.referralCount || 0}</h3>
                                    <span className="text-muted">users</span>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Header>
                            <Card.Title>Wallet Information</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-3">
                                <strong className="text-dark d-block mb-2">Wallet ID</strong>
                                <p className="mb-0">{user.wallet?._id || '-'}</p>
                            </div>
                            <div className="mb-3">
                                <strong className="text-dark d-block mb-2">Created On</strong>
                                <p className="mb-0">{formatDate(user.wallet?.createdAt)}</p>
                            </div>
                            <div className="mb-3">
                                <strong className="text-dark d-block mb-2">Last Updated</strong>
                                <p className="mb-0">{formatDate(user.wallet?.updatedAt)}</p>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Transactions/Trades Tabs */}
            <div className="row mt-3">
                <div className="col-xl-12">
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <Card.Title>
                                    {activeTab === 'transactions' ? 'Transaction History' : 'Trade History'}
                                </Card.Title>
                                <div>
                                    <Button
                                        variant={activeTab === 'transactions' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        className="me-2"
                                        onClick={() => setActiveTab('transactions')}
                                    >
                                        Transactions
                                    </Button>
                                    <Button
                                        variant={activeTab === 'trades' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setActiveTab('trades')}
                                    >
                                        Trades
                                    </Button>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {activeTab === 'transactions' ? (
                                <div className="table-responsive">
                                    <Table responsive>
                                        <thead>
                                            <tr>
                                                <th>Transaction ID</th>
                                                <th>Type</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center">No transactions found</td>
                                                </tr>
                                            ) : (
                                                transactions.map((transaction) => (
                                                    <tr key={transaction._id}>
                                                        <td>{transaction._id?.substring(0, 8)}...</td>
                                                        <td>
                                                            <Badge bg="info">{transaction.type || '-'}</Badge>
                                                        </td>
                                                        <td>{formatAmount(transaction.amount)}</td>
                                                        <td>
                                                            <Badge bg={getStatusBadge(transaction.status)}>
                                                                {transaction.status || '-'}
                                                            </Badge>
                                                        </td>
                                                        <td>{formatDate(transaction.createdAt)}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table responsive>
                                        <thead>
                                            <tr>
                                                <th>Trade ID</th>
                                                <th>Symbol</th>
                                                <th>Type</th>
                                                <th>Direction</th>
                                                <th>Entry Price</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trades.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center">No trades found</td>
                                                </tr>
                                            ) : (
                                                trades.map((trade) => (
                                                    <tr key={trade._id}>
                                                        <td>{trade._id?.substring(0, 8)}...</td>
                                                        <td>{trade.symbol?.name || trade.symbol || '-'}</td>
                                                        <td>
                                                            <Badge bg="info">{trade.type || '-'}</Badge>
                                                        </td>
                                                        <td>
                                                            <Badge bg={trade.direction === 'buy' ? 'success' : 'danger'}>
                                                                {trade.direction?.toUpperCase() || '-'}
                                                            </Badge>
                                                        </td>
                                                        <td>${trade.entryPrice?.toFixed(2) || '-'}</td>
                                                        <td>
                                                            <Badge bg={getStatusBadge(trade.status)}>
                                                                {trade.status || '-'}
                                                            </Badge>
                                                        </td>
                                                        <td>{formatDate(trade.createdAt)}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default UserDetails;

